import { Tag } from '@lumapps/tags/types';
import { TranslateObject } from '@lumapps/translations';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

/**
 * List of all legacy content types.
 *
 * This list was previously injected in the HTML by the backend, but we moved it here since it is not a dynamic list.
 * @see https://github.com/lumapps/lumapps-back/blob/master/app/server/content/__init__.py
 */
export const ContentTypes = {
    COMMUNITY: 'community',
    CUSTOM: 'custom',
    CUSTOM_LIST: 'custom_list',
    DIRECTORY: 'directory',
    IMAGE_GALLERY: 'image_gallery',
    MENU: 'menu',
    NEWS: 'news',
    NEWS_LIST: 'news_list',
    PAGE: 'page',
    POST: 'post',
    TUTORIAL: 'tutorial',
    USER_DIRECTORY: 'user_directory',
} as const;

export type ContentType = ObjectValues<typeof ContentTypes>;

export const CCT_SCOPE = 'custom-content-types';

export const BASIC_CONTENT_TYPES: string[] = Object.values(ContentTypes);

export const NON_DELETABLE_CONTENT_TYPES: string[] = [ContentTypes.PAGE, ContentTypes.NEWS];

/**
 * https://api.lumapps.com/docs/output/_schemas/customcontenttypetag.html
 */
export type CustomContentTypeTag = Tag;

/**
 * https://api.lumapps.com/docs/output/_schemas/customcontenttype.html
 * @see https://lumapps-api.stoplight.io/docs/lumapps-public-api/8b9c8568b10de-custom-content-type
 */
export type CustomContentType = {
    id: string;
    uid: string;
    name: TranslateObject;
    /** The icon of the custom content type. */
    icon: string;
    /** The list of tags of the custom content type. */
    tags: CustomContentTypeTag[];
    instance: string;
    customer: string;
    /** Whether the custom content type is heritable. */
    heritable?: boolean;
    /** Whether heritable modification is locked. */
    heritableLocked?: boolean;
    /** Whether the contents can't be created without a template.  */
    createFromScratchLocked?: boolean;
    /** Whether the publication workflow (need a review to publish a content)
     * is enabled for the custom content type. */
    isWorkflowEnabled?: boolean;
    /** Whether the end date is mandatory for this custom content type.  */
    isEndDateMandatory?: boolean;
    /** Whether the contributors are notified for any change concerning the custom content type. */
    notifyContributors?: boolean;
    /** The number of months used to automatically determinate the end date.
     * Acceptable values are 0 to 100, inclusive. */
    endDateDelta?: number;
    functionalInnerId?: string;
    /** Information regarding the source site of the CTT */
    instanceDetails?: {
        id: string;
        name: string;
        slug: string;
        title: TranslateObject;
    };
    status?: 'LIVE' | 'DELETED';
};

/**
 * Type used when creating a new Content Type, with optional id and uid
 */
export type CustomContentTypeCreationPayload = Omit<CustomContentType, 'id' | 'uid'> & {
    id?: string;
    uid?: string;
};

export type ContentTypeIdParameter = Record<'contentTypeId', CustomContentType['id']>;
